import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

export default function Navbar() {
  const { language, t, linkTo, changeLanguage } = useLanguage()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileYdelserOpen, setIsMobileYdelserOpen] = useState(false)
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false)
  const [activeYdelse, setActiveYdelse] = useState(null)
  const [mobileActiveYdelseSlug, setMobileActiveYdelseSlug] = useState(null)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { name: t('nav.home'), href: '#forside' },
    { name: t('nav.about'), href: '#om-os' },
    { name: t('nav.services'), href: '#ydelser', hasDropdown: true },
    { name: t('nav.collaboration'), href: '#samarbejde' },
    { name: t('nav.contact'), href: '/kontakt' },
  ]

  const ydelserList = language === 'da' ? [
    {
      name: 'Erhvervsrengøring',
      slug: 'professionel-erhvervsrengoering',
      desc: 'Professionel rengøring til erhverv & kontorer',
      subHeader: 'Vi tilbyder rengøring til bl.a.:',
      subItems: [
        { name: 'Kontorer', slug: 'professionel-kontorrengoering' },
        { name: 'Tandlægeklinikker', slug: 'hygiejnisk-klinikrengoering' },
        { name: 'Hospitaler & institutioner', slug: 'hospitaler-og-institutioner-rengoering' },
        { name: 'Skoler', slug: 'skole-og-institutionsrengoering' },
        { name: 'Dyreklinikker', slug: 'professionel-rengoering-af-dyreklinikker' },
        { name: 'Salon & klinikker', slug: 'rengoering-af-saloner-og-skonhedsklinikker' },
        { name: 'Svømmehaller', slug: 'svoemmehal-og-sportsrengoering' },
        { name: 'Banker', slug: 'bank-og-finansrengoering' },
        { name: 'Vinduespudsning', slug: 'erhvervs-vinduespudsning' }
      ]
    },
    {
      name: 'Specialrengøring',
      slug: 'skraeddersyet-specialrengoering',
      desc: 'Dybdegående rengøring, flytte- & slutrengøring',
      subHeader: 'Yderligere ydelser:',
      subItems: [
        { name: 'Flytterengøring', slug: 'grundig-flytterengoering' },
        { name: 'Sommerhusrengøring', slug: 'professionel-sommerhusrengoering' },
        { name: 'Højderengøring', slug: 'professionel-vinduespudsning' }
      ]
    },
    {
      name: 'Byggepladsservice',
      slug: 'effektiv-byggepladsservice',
      desc: 'Løbende oprydning & slutrengøring på byggepladser',
      subHeader: 'Yderligere ydelser:',
      subItems: [
        { name: 'Oprydning af byggepladser', slug: 'oprydning-af-byggepladser' },
        { name: 'Skurevognrengøring', slug: 'skurevognrengoering' }
      ]
    }
  ] : [
    {
      name: 'Commercial Cleaning',
      slug: 'professional-commercial-cleaning',
      desc: 'Professional cleaning for businesses & offices',
      subHeader: 'We offer cleaning for e.g.:',
      subItems: [
        { name: 'Offices', slug: 'professional-office-cleaning' },
        { name: 'Dental Clinics', slug: 'hygienic-clinic-cleaning' },
        { name: 'Hospitals & Institutions', slug: 'hospital-and-institution-cleaning' },
        { name: 'Schools', slug: 'school-and-institution-cleaning' },
        { name: 'Veterinary Clinics', slug: 'professional-veterinary-clinic-cleaning' },
        { name: 'Salons & Clinics', slug: 'salon-and-beauty-clinic-cleaning' },
        { name: 'Swimming Pools', slug: 'swimming-pool-and-sports-cleaning' },
        { name: 'Banks', slug: 'bank-and-financial-cleaning' },
        { name: 'Window Cleaning', slug: 'commercial-window-cleaning' }
      ]
    },
    {
      name: 'Specialty Cleaning',
      slug: 'tailored-specialty-cleaning',
      desc: 'Deep cleaning, move-out & post-construction cleaning',
      subHeader: 'Additional services:',
      subItems: [
        { name: 'Move-out Cleaning', slug: 'thorough-move-out-cleaning' },
        { name: 'Holiday Home Cleaning', slug: 'professional-holiday-home-cleaning' },
        { name: 'High-level Cleaning', slug: 'professional-window-cleaning' }
      ]
    },
    {
      name: 'Construction Site Service',
      slug: 'effective-construction-site-service',
      desc: 'Ongoing cleanup & post-construction cleaning on sites',
      subHeader: 'Additional services:',
      subItems: [
        { name: 'Construction Site Cleanup', slug: 'construction-site-cleanup' },
        { name: 'Construction Trailer Cleaning', slug: 'construction-trailer-cleaning' }
      ]
    }
  ]

  useEffect(() => {
    if (ydelserList.length > 0) {
      setActiveYdelse(ydelserList[0])
    }
  }, [language])

  const handleNavClick = (e, href) => {
    e.preventDefault()
    setIsMobileMenuOpen(false)
    setIsMobileYdelserOpen(false)

    if (href.startsWith('#')) {
      const isHome = location.pathname === '/' || location.pathname === '/en'
      if (isHome) {
        const element = document.querySelector(href)
        if (element) {
          const offset = 80 // height of navbar
          const bodyRect = document.body.getBoundingClientRect().top
          const elementRect = element.getBoundingClientRect().top
          const elementPosition = elementRect - bodyRect
          const offsetPosition = elementPosition - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      } else {
        navigate(linkTo(href))
      }
    } else {
      navigate(linkTo(href))
    }
  }

  const handleLangChange = (lang) => {
    changeLanguage(lang)
    setIsMobileMenuOpen(false)
  }

  const isHome = location.pathname === '/' || location.pathname === '/en'

  return (
    <header
      className={`fixed top-0 left-0 z-50 w-full transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-[#FAF8F5] border-b border-[#E6DFD3] py-4 shadow-sm'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link 
          to={linkTo('/')} 
          onClick={(e) => handleNavClick(e, '#forside')} 
          className="flex items-center gap-3 group"
        >
          <img 
            src="https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/75db1acc-4084-4ebe-9102-70c2bb934dac.png" 
            alt="Wecas ApS Logo" 
            className={`w-auto object-contain transition-all duration-300 origin-left ${
              isScrolled || !isHome
                ? 'h-15 sm:h-17 md:h-19 scale-100 group-hover:scale-105' 
                : 'h-18 sm:h-22 md:h-26 scale-110 md:scale-125 group-hover:scale-[1.3]'
            }`}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            if (link.hasDropdown) {
              return (
                <div
                  key={link.name}
                  className="relative group py-4"
                  onMouseEnter={() => setIsDesktopDropdownOpen(true)}
                  onMouseLeave={() => setIsDesktopDropdownOpen(false)}
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                    }}
                    className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-[#2C302E]/80 hover:text-[#a78458] transition-colors relative py-1 cursor-default"
                  >
                    <span>{link.name}</span>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${isDesktopDropdownOpen ? 'rotate-180 text-[#a78458]' : 'text-[#2C302E]/60'}`} />
                  </button>

                  {/* Dropdown Menu */}
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 top-full w-[520px] bg-[#FAF8F5] border border-[#E6DFD3] rounded-none p-4 shadow-xl transition-all duration-300 origin-top ${
                      isDesktopDropdownOpen
                        ? 'opacity-100 scale-100 translate-y-0 visible'
                        : 'opacity-0 scale-95 -translate-y-2 invisible pointer-events-none'
                    }`}
                  >
                    <div className="grid grid-cols-12 gap-4">
                      {/* Left Column: Main Services */}
                      <div className="col-span-5 border-r border-[#E6DFD3]/60 pr-4 flex flex-col gap-1">
                        <span className="text-[10px] font-bold tracking-wider text-[#2C302E]/40 uppercase mb-2">
                          {language === 'da' ? 'Kategorier' : 'Categories'}
                        </span>
                        {ydelserList.map((ydelse) => (
                          <Link
                            key={ydelse.slug}
                            to={linkTo(`/ydelser/${ydelse.slug}`)}
                            onMouseEnter={() => setActiveYdelse(ydelse)}
                            onClick={() => setIsDesktopDropdownOpen(false)}
                            className={`flex flex-col text-left py-2 px-3 transition-all group/item ${
                              activeYdelse?.slug === ydelse.slug
                                ? 'bg-[#a78458]/5 border-l-2 border-[#a78458] pl-2'
                                : 'border-l-2 border-transparent hover:bg-[#FAF8F5]/80'
                            }`}
                          >
                            <span className={`text-xs font-bold tracking-wider uppercase transition-colors ${
                              activeYdelse?.slug === ydelse.slug ? 'text-[#a78458]' : 'text-[#2C302E]'
                            }`}>
                              {ydelse.name}
                            </span>
                            <span className="text-[10px] text-[#2C302E]/60 mt-0.5 line-clamp-1">
                              {ydelse.desc}
                            </span>
                          </Link>
                        ))}
                      </div>

                      {/* Right Column: Specific Services */}
                      <div className="col-span-7 pl-2 flex flex-col">
                        {activeYdelse?.subItems && activeYdelse.subItems.length > 0 ? (
                          <>
                            <span className="text-[10px] font-bold tracking-wider text-[#2C302E]/40 uppercase mb-2">
                              {activeYdelse?.subHeader || (language === 'da' ? 'Specifikke ydelser' : 'Specific services')}
                            </span>
                            <div className="grid grid-cols-1 gap-1 max-h-[240px] overflow-y-auto pr-1">
                              {activeYdelse.subItems.map((subItem) => (
                                <Link
                                  key={subItem.name}
                                  to={linkTo(`/ydelser/${subItem.slug || activeYdelse.slug}`)}
                                  onClick={() => setIsDesktopDropdownOpen(false)}
                                  className="flex items-center py-1.5 px-2.5 hover:bg-[#a78458]/5 transition-colors group/sub"
                                >
                                  <span className="text-xs font-medium text-[#2C302E] group-hover/sub:text-[#a78458] transition-colors">
                                    {subItem.name}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-start justify-center h-full py-4 pr-2">
                            <span className="text-[10px] font-bold tracking-wider text-[#2C302E]/40 uppercase mb-2">
                              {language === 'da' ? 'Ydelsesinfo' : 'Service info'}
                            </span>
                            <p className="text-xs text-[#2C302E]/70 mb-4 leading-relaxed">
                              {language === 'da' 
                                ? 'Vi tilbyder komplet og professionel byggepladsservice tilpasset jeres specifikke behov.'
                                : 'We offer complete and professional construction site service tailored to your specific needs.'}
                            </p>
                            <Link
                              to={linkTo(`/ydelser/${activeYdelse?.slug}`)}
                              onClick={() => setIsDesktopDropdownOpen(false)}
                              className="text-xs font-bold text-[#a78458] hover:underline flex items-center gap-1"
                            >
                              {language === 'da' ? 'Læs mere om Byggepladsservice' : 'Read more about Construction Site Service'} &rarr;
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            }

            return (
              <a
                key={link.name}
                href={link.href.startsWith('#') ? link.href : linkTo(link.href)}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-xs uppercase tracking-wider font-semibold text-[#2C302E]/80 hover:text-[#a78458] transition-colors relative py-1 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 hover:after:w-full after:bg-[#a78458] after:transition-all after:duration-300"
              >
                {link.name}
              </a>
            )
          })}
        </nav>

        {/* Desktop CTA, Contact & Language Switcher */}
        <div className="hidden md:flex items-center gap-6">
          <a
            href="tel:53843361"
            className="hidden lg:flex items-center gap-2 text-sm font-medium text-[#2C302E] hover:text-[#a78458] transition-colors"
          >
            <Phone className="h-4 w-4 text-black" />
            <span>53 84 33 61</span>
          </a>
          <Link
            to={linkTo('/kontakt')}
            className="rounded-none bg-[#a78458] px-5 py-2.5 text-xs font-semibold tracking-wider text-[#FAF8F5] uppercase shadow-md shadow-[#a78458]/20 transition-all duration-300 hover:bg-[#917047] hover:shadow-lg hover:shadow-[#a78458]/30 hover:-translate-y-1.5 hover:scale-[1.03]"
          >
            {language === 'da' ? 'Få et tilbud' : 'Get a quote'}
          </Link>

          {/* Language Switcher */}
          <div className="flex items-center gap-1 border-l border-[#E6DFD3] pl-4">
            <button
              onClick={() => handleLangChange('da')}
              className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold tracking-wider transition-all duration-200 rounded-none ${
                language === 'da'
                  ? 'text-[#a78458] bg-[#a78458]/10 border border-[#a78458]/20'
                  : 'text-[#2C302E]/60 hover:text-[#2C302E] border border-transparent'
              }`}
            >
              <span>DK</span>
            </button>
            <button
              onClick={() => handleLangChange('en')}
              className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold tracking-wider transition-all duration-200 rounded-none ${
                language === 'en'
                  ? 'text-[#a78458] bg-[#a78458]/10 border border-[#a78458]/20'
                  : 'text-[#2C302E]/60 hover:text-[#2C302E] border border-transparent'
              }`}
            >
              <span>EN</span>
            </button>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden rounded-none p-2 text-[#2C302E] hover:bg-[#F3EFE6] transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-[#FAF8F5] border-b border-[#E6DFD3] py-6 px-6 shadow-lg animate-fadeIn max-h-[calc(100vh-80px)] overflow-y-auto">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => {
              if (link.hasDropdown) {
                return (
                  <div key={link.name} className="flex flex-col">
                    <button
                      onClick={() => setIsMobileYdelserOpen(!isMobileYdelserOpen)}
                      className="flex items-center justify-between text-base font-medium text-[#2C302E] py-2 border-b border-[#E6DFD3]/40 w-full text-left"
                    >
                      <span>{link.name}</span>
                      <ChevronDown className={`h-5 w-5 text-[#2C302E]/60 transition-transform duration-300 ${isMobileYdelserOpen ? 'rotate-180 text-[#a78458]' : ''}`} />
                    </button>
                    
                    {/* Mobile Dropdown Items */}
                    <div className={`flex flex-col gap-3 pl-4 overflow-hidden transition-all duration-300 ${isMobileYdelserOpen ? 'max-h-[500px] mt-2 mb-1' : 'max-h-0'}`}>
                      {ydelserList.map((ydelse) => {
                        const isSubOpen = mobileActiveYdelseSlug === ydelse.slug
                        const hasSubItems = ydelse.subItems && ydelse.subItems.length > 0

                        if (!hasSubItems) {
                          return (
                            <Link
                              key={ydelse.slug}
                              to={linkTo(`/ydelser/${ydelse.slug}`)}
                              onClick={() => {
                                setIsMobileMenuOpen(false)
                                setIsMobileYdelserOpen(false)
                              }}
                              className="flex items-center justify-between py-1.5 text-sm font-semibold text-[#2C302E] hover:text-[#a78458] text-left w-full"
                            >
                              <span>{ydelse.name}</span>
                            </Link>
                          )
                        }

                        return (
                          <div key={ydelse.slug} className="flex flex-col">
                            <button
                              onClick={() => setMobileActiveYdelseSlug(isSubOpen ? null : ydelse.slug)}
                              className="flex items-center justify-between py-1.5 text-sm font-semibold text-[#2C302E] hover:text-[#a78458] text-left w-full"
                            >
                              <span>{ydelse.name}</span>
                              <ChevronDown className={`h-4 w-4 text-[#2C302E]/50 transition-transform duration-300 ${isSubOpen ? 'rotate-180 text-[#a78458]' : ''}`} />
                            </button>
                            
                            {/* Sub-items list */}
                            <div className={`flex flex-col gap-2 pl-3 border-l border-[#E6DFD3] overflow-hidden transition-all duration-300 ${isSubOpen ? 'max-h-64 mt-1 mb-2' : 'max-h-0'}`}>
                              <div className="text-[10px] font-bold text-[#2C302E]/40 uppercase pt-1">
                                {ydelse.subHeader}
                              </div>
                              {ydelse.subItems.map((subItem) => (
                                <Link
                                  key={subItem.name}
                                  to={linkTo(`/ydelser/${subItem.slug || ydelse.slug}`)}
                                  onClick={() => {
                                    setIsMobileMenuOpen(false)
                                    setIsMobileYdelserOpen(false)
                                    setMobileActiveYdelseSlug(null)
                                  }}
                                  className="flex items-center py-1 text-xs text-[#2C302E]/80 hover:text-[#a78458]"
                                >
                                  <span>{subItem.name}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              }

              return (
                <a
                  key={link.name}
                  href={link.href.startsWith('#') ? link.href : linkTo(link.href)}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-base font-medium text-[#2C302E] hover:text-[#a78458] py-2 border-b border-[#E6DFD3]/40"
                >
                  {link.name}
                </a>
              )
            })}

            {/* Language Switcher in Mobile Menu */}
            <div className="flex flex-col gap-2 py-2 border-b border-[#E6DFD3]/40">
              <span className="text-xs font-bold text-[#2C302E]/40 uppercase mb-1">
                {t('nav.select_lang')}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleLangChange('da')}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold tracking-wider transition-all duration-200 border w-full justify-center rounded-none ${
                    language === 'da'
                      ? 'text-[#a78458] bg-[#a78458]/10 border-[#a78458]/20'
                      : 'text-[#2C302E]/60 hover:text-[#2C302E] border-transparent bg-black/[0.02]'
                  }`}
                >
                  <span>DK</span>
                </button>
                <button
                  onClick={() => handleLangChange('en')}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold tracking-wider transition-all duration-200 border w-full justify-center rounded-none ${
                    language === 'en'
                      ? 'text-[#a78458] bg-[#a78458]/10 border-[#a78458]/20'
                      : 'text-[#2C302E]/60 hover:text-[#2C302E] border-transparent bg-black/[0.02]'
                  }`}
                >
                  <span>EN</span>
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-4 pt-4">
              <a
                href="tel:53843361"
                className="flex items-center gap-3 text-sm font-medium text-[#2C302E] py-2"
              >
                <Phone className="h-5 w-5 text-[#a78458]" />
                <span>{language === 'da' ? 'Ring døgnet rundt: 53 84 33 61' : 'Call 24/7: 53 84 33 61'}</span>
              </a>
              <a
                href="mailto:info@wecas.dk"
                className="flex items-center gap-3 text-sm font-medium text-[#2C302E] py-2"
              >
                <Mail className="h-5 w-5 text-[#a78458]" />
                <span>{language === 'da' ? 'Skriv til os: info@wecas.dk' : 'Email us: info@wecas.dk'}</span>
              </a>
              <Link
                to={linkTo('/kontakt')}
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full text-center rounded-none bg-[#a78458] py-3 text-sm font-semibold tracking-wider text-[#FAF8F5] uppercase shadow-md shadow-[#a78458]/20 transition-all duration-300 hover:bg-[#917047] hover:-translate-y-1 hover:scale-[1.02] hover:shadow-lg hover:shadow-[#a78458]/30"
              >
                {language === 'da' ? 'Få et uforpligtende tilbud' : 'Get a free quote'}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
